import { useState, useEffect } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import Modal from './Modal';
import { unseenChanges, changeTourId } from '../../utils/changeLog';

// ── What has changed since you were last here ────────────────────────────────
//
// Somebody opens ops to approve a holiday, goes to where that page has always
// been, and it is not there. A guided tour cannot help them: a tour lives on
// the page they are failing to find. So this is mounted once for the whole
// app and shows wherever they land, listing what has moved, been renamed or
// been taken away since they joined, newest first.
//
// It is not a release note and deliberately does not list everything we ship.
// The changelog it reads (utils/changeLog) holds only changes somebody could
// go looking for and fail to find, which is what keeps it short enough to be
// read rather than dismissed.
//
// Nobody is told about a change that happened before they arrived: they never
// saw the old arrangement, and "this used to be under People" describes a
// world they never knew. Seen-ness is per change, so an entry added next month
// still reaches somebody who read this one.
export default function WhatsChanged() {
  const { currentUser } = useApp();
  const [entries, setEntries] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    // date_of_joining is what the rule turns on, and it arrives with the
    // person rather than on the first render, so there is nothing to work out
    // until it is here.
    if (!currentUser?.date_of_joining) return;
    let alive = true;
    api.get('feature-tours')
      .then((rows) => {
        if (!alive) return;
        const seen = (rows || []).map((r) => r.tour_id);
        setEntries(unseenChanges(currentUser, seen));
      })
      // Staying quiet once beats announcing a move to somebody who has already
      // read about it, so a failed check shows nothing.
      .catch(() => {});
    return () => { alive = false; };
  }, [currentUser]);

  if (!entries.length) return null;
  const entry = entries[Math.min(idx, entries.length - 1)];
  const last = idx >= entries.length - 1;

  // Recorded as it is read rather than all at the end, so somebody who closes
  // the window half way through is not told about the first one again.
  const markSeen = (e) => api.put(`feature-tours/${changeTourId(e.id)}`, { status: 'completed' }).catch(() => {});

  const close = async () => {
    // Dismissing settles the lot. Somebody who shuts this has decided they do
    // not want the rest of it, and re-opening it on their next page load would
    // be the app arguing with them.
    await Promise.all(entries.map(markSeen));
    setEntries([]);
  };

  const next = async () => {
    await markSeen(entry);
    if (last) { setEntries([]); return; }
    setIdx(idx + 1);
  };

  const goThere = async () => {
    await markSeen(entry);
    const page = entry.page;
    setEntries([]);
    if (page) window.location.hash = `#${page}`;
  };

  return (
    <Modal title="Some things have moved" onClose={close}
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="text-[9px] text-gray-400">
            {entries.length > 1 ? `${Math.min(idx + 1, entries.length)} of ${entries.length} changes since you last looked` : 'One change since you last looked'}
          </span>
          <div className="flex gap-2">
            <button onClick={close} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">
              Dismiss
            </button>
            {entry.page && (
              <button onClick={goThere} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">
                Take me there
              </button>
            )}
            <button onClick={next} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">
              {last ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-2">
        <div>
          <div className="text-[11px] font-extrabold text-navy">{entry.title}</div>
          <div className="text-[9px] text-gray-500">{entry.date}</div>
        </div>
        {entry.body.map((para, i) => (
          <p key={i} className="text-[11px] text-gray-700 leading-relaxed">{para}</p>
        ))}
      </div>
    </Modal>
  );
}
